/** Vocabulário da importação de catálogo, compartilhado pelas duas
 *  telas que dependem dele — os Ajustes (que a executa) e o detalhe do
 *  upload (que só precisa saber se há uma rodando, pra não deixar
 *  reprocessar a fila em cima de um catálogo que ainda está mudando).
 *
 *  Mora em `shared/` pelo mesmo motivo que `upload-shared.ts` existe:
 *  para que nenhuma das duas features precise importar a outra. */

import type { CatalogImport } from "@/shared/api/api-client";

/** Os dois status em que o import ainda está mexendo no catálogo. São
 *  os mesmos que a trava de execução do banco usa (índice único parcial
 *  sobre `QUEUED`/`STARTED`), então "ativo" aqui e "ativo" lá são a
 *  mesma coisa por construção — não duas definições que podem divergir. */
export const ACTIVE_IMPORT_STATUSES = new Set(["QUEUED", "STARTED"]);

export function isImportActive(run: CatalogImport): boolean {
  return ACTIVE_IMPORT_STATUSES.has(run.status);
}

/** `FINISHED_WITH_REJECTIONS` tem nome próprio de propósito: "terminou
 *  com recusa" não é "terminou", e a tela precisa oferecer o download
 *  das recusadas em vez de dizer que deu tudo certo. */
export const IMPORT_STATUS_META: Record<
  string,
  { label: string; tone: "" | "success" | "warning" }
> = {
  QUEUED: { label: "Na fila", tone: "" },
  STARTED: { label: "Aplicando", tone: "" },
  FINISHED: { label: "Concluída", tone: "success" },
  FINISHED_WITH_REJECTIONS: { label: "Concluída com recusas", tone: "warning" },
  CANCELLED: { label: "Cancelada", tone: "warning" },
  ERRORED: { label: "Erro", tone: "warning" }
};

export function importStatusMeta(status: string) {
  return IMPORT_STATUS_META[status] ?? { label: status, tone: "" as const };
}

/** Quanto do arquivo já foi aplicado, em %. Serve pra barra da tela.
 *
 *  Conta pelo que FALTA (`pending_count`) e não pela soma dos quatro
 *  outcomes: são a mesma informação, mas o servidor manda o pendente
 *  pronto e somar quatro números no cliente é mais um lugar pra
 *  divergir do recibo. */
export function importProgressPercent(run: CatalogImport): number {
  if (!run.total_rows) return 0;
  const done = run.total_rows - run.pending_count;
  return Math.min(100, Math.max(0, Math.round((done / run.total_rows) * 100)));
}
